import { BenefitLayout } from "providers/benefit-layout";
import React, { FC, useEffect, useState } from "react";
import { Button, ButtonVariant } from "shared/components/Button";
import { View } from "../../../../types";
import { toast } from "react-toastify";
import { DateInput } from "shared/components/forms/DateInput";
import api from "api";
import { useForm } from "shared/hooks/use-form";
import { Input } from "shared/components/forms/Input";
import { SuccessScreenView } from "../../../../components/SuccessScreen";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { leaseDetailsValidations } from "../../../../validations";
import { Money } from "types/money";

type Props = {
  setView: (view: View) => void;
  monthlyRentAmount: Money;
};

enum Steps {
  UpdateDetails = "UpdateDetails",
  Success = "Success"
}

export const UpdateLeaseDetailsView: FC<Props> = ({
  setView,
  monthlyRentAmount
}) => {
  const { register, onSubmit, handleChange } = useForm(
    {
      leaseEndDate: "",
      rentAmount: ""
    },
    leaseDetailsValidations
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(Steps.UpdateDetails);

  useEffect(() => {
    handleChange("rentAmount", () => (monthlyRentAmount.cents || 0) / 100)({});
  }, [monthlyRentAmount]);

  const { mutate } = useMutation({
    mutationKey: ["UPDATE_LEASE_DETAILS", id],
    mutationFn: ({
      lease_end_date,
      monthly_rent_amount
    }: {
      lease_end_date: string;
      monthly_rent_amount: {
        cents: number;
        currency: "USD";
      };
    }) =>
      api
        .updateLeaseDetails({
          id: Number(id),
          lease_end_date,
          monthly_rent_amount
        })
        .then(() => setStep(Steps.Success))
  });

  if (step === Steps.UpdateDetails)
    return (
      <BenefitLayout
        hasBack={true}
        title="Please update your lease details"
        mainClassName="max-w-[440px] w-full mx-auto md:mt-4"
        footer={
          <div className="max-w-[440px] mx-auto w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              type="submit"
              form="lease-details-form"
            >
              Done
            </Button>
            <Button
              className="w-full md:block hidden mt-2"
              onClick={() => setView(View.SelectView)}
            >
              Back
            </Button>
          </div>
        }
      >
        <form
          id="lease-details-form"
          onSubmit={onSubmit({
            onSuccess: (data) => {
              mutate({
                lease_end_date: data.leaseEndDate,
                monthly_rent_amount: {
                  cents: data.rentAmount * 100,
                  currency: "USD"
                }
              });
            },
            onError: (e) => {
              toast.error(Object.values(e).join(", "));
            }
          })}
        >
          <DateInput
            id="lease_end_date"
            label="Lease end"
            hideOptional
            {...register("leaseEndDate")}
          />
          <Input
            id="rentAmount"
            label="Rent amount"
            placeholder="Rent amount"
            {...register("rentAmount")}
            type="money"
            hideOptional
          />
        </form>
      </BenefitLayout>
    );

  return (
    <SuccessScreenView
      title="We’ve updated your lease end date!"
      subtext1="Please click below to verify your missing payments"
      buttonTitle="Verify payments"
      onButtonClick={() => navigate("/rent_reporting/issues/verify-rent")}
    />
  );
};
